import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { canManagePlaylists } from "@/lib/permissions";

type Playlist = {
  id: string;
  name: string;
  description: string | null;
  type: string;
  createdAt: string;
  // Audit V1
  visibility?: string;
  roles?: string[];
  publicSlug?: string | null;
};

export function PlaylistsPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canEdit = canManagePlaylists(user?.role);
  const [pendingDelete, setPendingDelete] = useState<Playlist | null>(null);
  const { data } = useQuery({
    queryKey: ["playlists"],
    queryFn: () => api.get<{ items: Playlist[] }>("/api/admin/playlists"),
  });
  const clone = useMutation({
    mutationFn: (id: string) => api.post(`/api/admin/playlists/${id}/clone`, {}),
    onSuccess: () => { toast.success("Cloned"); qc.invalidateQueries({ queryKey: ["playlists"] }); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/playlists/${id}`),
    onSuccess: () => {
      toast.success("Deleted");
      setPendingDelete(null);
      qc.invalidateQueries({ queryKey: ["playlists"] });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        toast.error(
          "Playlist has been used in a sent Demo Pack — can't delete. Try renaming or just leave it.",
          { duration: 8000 },
        );
      } else {
        toast.error(err instanceof Error ? err.message : "Delete failed");
      }
    },
  });

  return (
    <div className="space-y-4 max-w-6xl">
      <div className="flex items-center justify-between">
        <h1 className="font-head text-2xl font-semibold">Playlists</h1>
        {canEdit && (
          <Link to="/admin/playlists/new">
            <Button>New playlist</Button>
          </Link>
        )}
      </div>
      <Card>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-admin-rule">
              <th className="p-3 font-medium">Name</th>
              <th className="p-3 font-medium">Type</th>
              <th className="p-3 font-medium">Created</th>
              <th className="p-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data?.items.map((p) => (
              <tr key={p.id} className="border-b border-admin-rule">
                <td className="p-3">
                  <Link to={`/admin/playlists/${p.id}`} className="font-medium hover:text-gold">
                    {p.name}
                  </Link>
                  {/* Audit V1: surface visibility + roles in the list */}
                  {p.visibility && p.visibility !== "private" && (
                    <Badge variant="outline" className="ml-2">{p.visibility}</Badge>
                  )}
                  {(p.roles ?? []).map((r) => (
                    <Badge key={r} variant="outline" className="ml-1">{r}</Badge>
                  ))}
                </td>
                <td className="p-3"><Badge variant="outline">{p.type}</Badge></td>
                <td className="p-3 text-muted-foreground text-xs">{new Date(p.createdAt).toLocaleDateString()}</td>
                <td className="p-3 text-right space-x-2">
                  {canEdit ? (
                    <>
                      <Link to={`/admin/playlists/${p.id}`}>
                        <Button size="sm" variant="ghost">Edit</Button>
                      </Link>
                      <Button size="sm" variant="ghost" onClick={() => clone.mutate(p.id)}>Clone</Button>
                      <Button size="sm" variant="ghost" className="text-destructive hover:bg-destructive/10" onClick={() => setPendingDelete(p)}>Delete</Button>
                    </>
                  ) : (
                    <Link to={`/admin/playlists/${p.id}`}>
                      <Button size="sm" variant="ghost">View</Button>
                    </Link>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete "{pendingDelete?.name}"?</DialogTitle>
            <DialogDescription>
              This removes the playlist definition. Past Demo Packs that used it remain unchanged
              (they snapshot the playlist at send time).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPendingDelete(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => pendingDelete && remove.mutate(pendingDelete.id)} disabled={remove.isPending}>
              {remove.isPending ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
